import React, { useEffect, useRef, useState } from "react";
import "./OckClonePanel.css";

export default function OckClonePanel({
  open,
  sourcePoint,
  points = [],
  saving = false,
  onChangeInventory,
  onRemovePoint,
  onFocusPoint,
  onSave,
  onCancel,
  isMobile = false,
}) {
  const panelRef = useRef(null);
  const draggingRef = useRef(false);
  const offsetRef = useRef({ x: 0, y: 0 });

  const [position, setPosition] = useState({ x: 16, y: 16 });

  useEffect(() => {
    if (!open || isMobile) return;
    setPosition({ x: 16, y: 16 });
  }, [open, isMobile]);

  useEffect(() => {
    if (!open || isMobile) return;

    const handleMouseMove = (e) => {
      if (!draggingRef.current) return;

      const panel = panelRef.current;
      const parent = getBoundsParent();
      if (!panel || !parent) return;

      const panelRect = panel.getBoundingClientRect();

      const rawX = e.clientX - offsetRef.current.parentLeft - offsetRef.current.x;
      const rawY = e.clientY - offsetRef.current.parentTop - offsetRef.current.y;

      const maxX = Math.max(8, parent.clientWidth - panelRect.width - 8);
      const maxY = Math.max(8, parent.clientHeight - panelRect.height - 8);

      const nextX = Math.min(Math.max(8, rawX), maxX);
      const nextY = Math.min(Math.max(8, rawY), maxY);

      setPosition({ x: nextX, y: nextY });
    };

    const handleMouseUp = () => {
      draggingRef.current = false;
      document.body.style.userSelect = "";
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [open, isMobile]);

  const getBoundsParent = () => {
    return panelRef.current?.offsetParent || panelRef.current?.parentElement || null;
  };

  const handleDragStart = (e) => {
    if (isMobile) return;

    const panel = panelRef.current;
    const parent = getBoundsParent();
    if (!panel || !parent) return;

    const panelRect = panel.getBoundingClientRect();
    const parentRect = parent.getBoundingClientRect();

    draggingRef.current = true;
    offsetRef.current = {
      x: e.clientX - panelRect.left,
      y: e.clientY - panelRect.top,
      parentLeft: parentRect.left,
      parentTop: parentRect.top,
    };

    document.body.style.userSelect = "none";
  };

  if (!open) return null;

  return (
    <div
      ref={panelRef}
      className={`ock-clone-panel ${isMobile ? "ock-clone-panel--mobile" : ""}`}
      style={isMobile ? undefined : { left: position.x, top: position.y }}
    >
      <div
        className="ock-clone-panel__header"
        onMouseDown={handleDragStart}
      >
        <div className="ock-clone-panel__title">Клонирование точки ОСК</div>
        {!isMobile && <div className="ock-clone-panel__drag-hint">⋮⋮</div>}
      </div>

      <div className="ock-clone-panel__section">
        <div className="ock-clone-panel__section-title">Главная точка</div>
        <div className="ock-clone-panel__card">
          <div><b>ID:</b> {sourcePoint?.id ?? "—"}</div>
          <div><b>Адрес:</b> {sourcePoint?.address || "—"}</div>
          <div><b>Инв. №:</b> {sourcePoint?.inventory_number || "—"}</div>
        </div>
      </div>

      <div className="ock-clone-panel__section">
        <div className="ock-clone-panel__section-title">
          Клонированные точки ({points.length})
        </div>

        {points.length === 0 ? (
          <div className="ock-clone-panel__empty">
            Нажмите на карту, чтобы добавить точки
          </div>
        ) : (
          <div className="ock-clone-panel__list-viewport">
            <div className="ock-clone-panel__list">
              {points.map((pt, index) => (
                <div key={pt.tempId} className="ock-clone-panel__item">
                  <div className="ock-clone-panel__item-top">
                    <div className="ock-clone-panel__item-title">
                      Точка {index + 1}
                    </div>

                    <div className="ock-clone-panel__item-actions">
                      <button
                        type="button"
                        className="ock-clone-panel__mini-btn"
                        onClick={() => onFocusPoint?.(pt.tempId)}
                      >
                        Показать
                      </button>
                      <button
                        type="button"
                        className="ock-clone-panel__mini-btn ock-clone-panel__mini-btn--danger"
                        onClick={() => onRemovePoint?.(pt.tempId)}
                      >
                        Удалить
                      </button>
                    </div>
                  </div>

                  <div className="ock-clone-panel__item-meta">
                    <div><b>Адрес:</b> {pt.address || "—"}</div>
                    <div><b>Ш:</b> {Number(pt.lat).toFixed(6)}</div>
                    <div><b>Д:</b> {Number(pt.long).toFixed(6)}</div>
                  </div>

                  <input
                    className="ock-clone-panel__input"
                    value={pt.inventory_number || ""}
                    onChange={(e) => onChangeInventory?.(pt.tempId, e.target.value)}
                    placeholder="Инвентарный номер"
                  />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="ock-clone-panel__footer">
        <button
          type="button"
          className="ock-clone-panel__btn ock-clone-panel__btn--cancel"
          onClick={onCancel}
          disabled={saving}
        >
          Отменить
        </button>

        <button
          type="button"
          className="ock-clone-panel__btn ock-clone-panel__btn--save"
          onClick={onSave}
          disabled={saving || points.length === 0}
        >
          {saving ? "Сохранение..." : "Сохранить"}
        </button>
      </div>
    </div>
  );
}